import React, { useEffect, useState } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    Switch,
    Typography,
    Upload,
    Flex,
    message,
    Popover,
    Button
} from 'antd';
import { CloseOutlined, InboxOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import Dragger from 'antd/es/upload/Dragger';
import { Tooltip } from 'recharts';

const { Title } = Typography;
const { Option } = Select;

const UpdateModalForm = ({ open, onCancel, onSubmit, groupPermission, emailPermission, isMobile, formLoading, selectedRecord }) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [formData, setFormData] = useState({ is_published: true });

    const handleSwitchChange = (checked) => {
        setFormData((prev) => ({ ...prev, is_published: checked }));
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const formDataToSend = new FormData();

            // Add form fields to FormData
            formDataToSend.append('title', values.title);
            formDataToSend.append('description', values.description);
            formDataToSend.append('is_published', formData.is_published ? 'True' : 'False');

            values.group_permission?.forEach((uid) => {
                formDataToSend.append("group_permission", uid);
            });
            values.email_permission?.forEach((uid) => {
                formDataToSend.append("email_permission", uid);
            });

            // Add file to FormData if available
            if (fileList.length > 0) {
                fileList.forEach((file) => {
                    formDataToSend.append('files', file.originFileObj);
                });
            }

            const response = await onSubmit(formDataToSend);
            if (response.status === 201 && response.data.status === "success") {
                form.resetFields();
                setFileList([]);
                setFormData({ is_published: true });
                onCancel();
            }
        } catch (error) {
            message.error("Please check all required fields.");
        }
    };

    return (
        <Modal
            open={open}
            className="create-order"
            onOk={handleOk}
            onCancel={() => {
                form.resetFields();
                setFileList([]);
                setFormData({ is_published: true });
                onCancel();
            }}
            footer={[
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    gap: '20px',
                }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div>
                            <label>
                                Publish {!isMobile && 'Update'}
                            </label>
                        </div>
                        <Switch
                            checked={formData.is_published}
                            onChange={handleSwitchChange}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Button
                            key="submit"
                            type="primary"
                            onClick={handleOk}
                            loading={formLoading}
                        >
                            {formLoading ? 'Loading...' : 'Submit'}
                        </Button>
                        <Button
                            key="cancel"
                            onClick={() => {
                                form.resetFields();
                                setFileList([]);
                                setFormData({ is_published: true });
                                onCancel();
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            ]}
            width={800}
            maskClosable={false}
            okText={formLoading ? "Loading..." : "Submit"}
            closable={false}
            title={
                <div className="custom-modal-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Title level={3} style={{ color: 'white', margin: 0, position: 'relative', left: '12px' }}>
                        Add New Update
                    </Title>
                    <div style={{ marginRight: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <CloseOutlined
                            onClick={() => {
                                form.resetFields();
                                setFileList([]);
                                setFormData({ is_published: true });
                                onCancel();
                            }}
                            style={{ fontSize: '16px', cursor: 'pointer', color: 'white' }}
                        />
                    </div>
                </div>
            }
            bodyStyle={{
                maxHeight: '60vh', // You can tweak height like 70vh, 500px etc.
                overflowY: 'auto',
                paddingRight: '12px'
            }}
        >
            <Form form={form} layout="vertical">
                <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                    <Input placeholder="Enter update title" />
                </Form.Item>

                <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                    <ReactQuill style={{ height: '150px' }} placeholder="Enter Description..." />
                </Form.Item>

                <Form.Item
                    name="group_permission"
                    label="Share with Group"
                    rules={[]}
                    style={{ marginTop: isMobile ? '80px' : '50px' }}
                >
                    <Select
                        mode="multiple"
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                        placeholder="Select group">
                        {groupPermission.map(({ uid, name }) => (
                            <Option key={uid} value={uid}>{name}</Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="email_permission"
                    label="Share with Individuals"
                    rules={[]}
                >
                    <Select
                        mode="multiple"
                        showSearch
                        optionLabelProp="label"
                        placeholder="Select email"
                        filterOption={(input, option) => {
                            const name = option?.name?.toLowerCase() || '';
                            const email = option?.email?.toLowerCase() || '';
                            return name.includes(input.toLowerCase()) || email.includes(input.toLowerCase());
                        }}
                    >
                        {emailPermission.map(({ uid, email, name }) => (
                            <Option
                                key={uid}
                                value={uid} // 👈 sending UID to backend
                                label={email} // 👈 showing email in tag
                                name={name}  // 👈 used for searching
                                email={email} // 👈 used for searching
                            >
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '500', fontSize: '14px' }}>{name}</span>
                                    <span style={{ color: '#888', fontSize: '12px' }}>{email}</span> {/* softer color for email */}
                                </div>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="file"
                    label="Upload Files"
                >
                    <Dragger
                        fileList={fileList}
                        beforeUpload={() => false}
                        onChange={(info) => setFileList(info.fileList)}
                        multiple={true} // Allow multiple files
                        maxCount={10} // Set a max count if needed
                    >
                        <Flex align="center" justify="center" gap={20}>
                            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                            <p className="ant-upload-text">Click or drag files to this area to upload</p>
                        </Flex>
                    </Dragger>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default UpdateModalForm;
